import React, { useState } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icon,
} from '@eluve/components';

import { ProvisionPrompt } from '../tenant/components/ProvisionPrompt';

import { useActionsResults } from './providers/actionsProvider';
import { useAppointmentResults } from './providers/appointmentsProvider';
import { useTabsResults } from './providers/tabsProvider';
import { useTenantResults } from './providers/tenantsProvider';
import { CommandProviderState, CommandResult } from './types';

type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const renderGroup = (
  heading: string,
  state: CommandProviderState,
  onSelected: (result: CommandResult) => void,
) => (
  <CommandGroup heading={heading}>
    {state.results.map((result) => {
      const trailing = result.confirmation
        ? 'Requires provisioning'
        : result.description;
      return (
        <CommandItem
          key={result.id}
          value={result.id}
          onSelect={() => onSelected(result)}
        >
          {result.icon && <Icon name={result.icon} />}
          <span>{result.label}</span>
          {trailing && (
            <span className="ml-auto text-xs text-muted-foreground">
              {trailing}
            </span>
          )}
        </CommandItem>
      );
    })}
  </CommandGroup>
);

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onOpenChange,
}) => {
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<CommandResult | null>(null);
  const [running, setRunning] = useState(false);

  // Providers are hooks; calling each one at the top level satisfies
  // rules-of-hooks while still letting us iterate the section list below.
  // To add a new provider: import its hook, call it, append to `sections`.
  const tabs = useTabsResults(query);
  const tenants = useTenantResults(query);
  const appointments = useAppointmentResults(query);
  const actions = useActionsResults(query);

  const sections: Array<{ heading: string; state: CommandProviderState }> = [
    { heading: 'Tabs', state: tabs },
    { heading: 'Organizations', state: tenants },
    { heading: 'Appointments', state: appointments },
    { heading: 'Actions', state: actions },
  ];

  const isAnyLoading = sections.some(({ state }) => state.loading);
  const hasAnyResults = sections.some(({ state }) => state.results.length > 0);

  const closeAndReset = () => {
    onOpenChange(false);
    setQuery('');
    setPending(null);
    setRunning(false);
  };

  const handleSelected = async (result: CommandResult) => {
    // Privilege-elevating results stop here — the user must click the
    // confirm button before we actually fire `onSelect`.
    if (result.confirmation) {
      setPending(result);
      return;
    }
    closeAndReset();
    await result.onSelect();
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setRunning(true);
    try {
      await pending.onSelect();
    } finally {
      closeAndReset();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : closeAndReset())}
    >
      <DialogContent
        size="lg"
        className="top-1/3 translate-y-0 overflow-hidden rounded-xl! p-0"
        showCloseButton={false}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Command Palette</DialogTitle>
          <DialogDescription>
            Jump to a tab, organization, or appointment.
          </DialogDescription>
        </DialogHeader>
        {pending ? (
          <ProvisionPrompt
            title={pending.confirmation!.title}
            detail={pending.confirmation!.detail}
            confirmLabel={pending.confirmation!.confirmLabel}
            onConfirm={handleConfirm}
            onCancel={() => setPending(null)}
            running={running}
          />
        ) : (
          /*
           * `shouldFilter={false}` because each provider filters server-side
           * or in-memory. Letting cmdk filter would drop GraphQL hits whose
           * id/label doesn't textually contain the query string.
           */
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Jump to a tab, organization, or appointment…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {isAnyLoading && !hasAnyResults ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading…
                </div>
              ) : (
                <CommandEmpty>No results found.</CommandEmpty>
              )}
              {sections
                .filter(({ state }) => state.results.length > 0)
                .map(({ heading, state }, index) => (
                  <React.Fragment key={heading}>
                    {index > 0 && <CommandSeparator />}
                    {renderGroup(heading, state, handleSelected)}
                  </React.Fragment>
                ))}
            </CommandList>
          </Command>
        )}
      </DialogContent>
    </Dialog>
  );
};
